import { forwardRef, ImgHTMLAttributes } from 'react'
import { clsx } from 'clsx'

interface AvatarProps extends ImgHTMLAttributes<HTMLImageElement> {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  name?: string
  status?: 'online' | 'offline' | 'busy' | 'away'
}

const Avatar = forwardRef<HTMLImageElement, AvatarProps>(
  ({ className, size = 'md', name, src, alt, status, ...props }, ref) => {
    const sizes = {
      xs: 'w-6 h-6 text-xs',
      sm: 'w-8 h-8 text-sm',
      md: 'w-10 h-10 text-sm',
      lg: 'w-12 h-12 text-base',
      xl: 'w-16 h-16 text-lg',
    }

    const statusColors = {
      online: 'bg-green-400',
      offline: 'bg-gray-400',
      busy: 'bg-red-400',
      away: 'bg-yellow-400',
    }

    const statusSizes = {
      xs: 'w-1.5 h-1.5',
      sm: 'w-2 h-2',
      md: 'w-2.5 h-2.5',
      lg: 'w-3 h-3',
      xl: 'w-4 h-4',
    }

    // Genera iniziali dal nome
    const getInitials = (name: string): string => {
      const words = name.trim().split(' ')
      if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase()
      }
      return (words[0][0] + words[words.length - 1][0]).toUpperCase()
    }

    // Genera colore dal nome
    const getColorFromName = (name: string): string => {
      const colors = [
        'bg-red-500',
        'bg-orange-500',
        'bg-amber-500',
        'bg-yellow-500',
        'bg-lime-500',
        'bg-green-500',
        'bg-emerald-500',
        'bg-teal-500',
        'bg-cyan-500',
        'bg-sky-500',
        'bg-blue-500',
        'bg-indigo-500',
        'bg-violet-500',
        'bg-purple-500',
        'bg-fuchsia-500',
        'bg-pink-500',
        'bg-rose-500',
      ]
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      return colors[Math.abs(hash) % colors.length]
    }

    return (
      <div className="relative inline-block">
        {src ? (
          <img
            ref={ref}
            src={src}
            alt={alt || name || 'Avatar'}
            className={clsx(
              'rounded-full object-cover',
              sizes[size],
              className
            )}
            {...props}
          />
        ) : (
          <div
            className={clsx(
              'rounded-full flex items-center justify-center font-medium text-white',
              sizes[size],
              name ? getColorFromName(name) : 'bg-gray-400',
              className
            )}
          >
            {name ? getInitials(name) : '?'}
          </div>
        )}
        {status && (
          <span
            className={clsx(
              'absolute bottom-0 right-0 rounded-full ring-2 ring-white',
              statusColors[status],
              statusSizes[size]
            )}
          />
        )}
      </div>
    )
  }
)

Avatar.displayName = 'Avatar'

export default Avatar
